import { useEffect, useState } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import {
    DECK_HINT_SEEN,
    deviceMemory,
    hasSeen,
    rememberSeen,
} from '../../application/onboarding/seenOnce';
import { BackIcon, ForwardIcon } from '../icons/ControlIcons';

/**
 * The one time this app says the deck can be swiped.
 *
 * The gesture is how a reader gets from one car to the next and nothing on the
 * card announces it: the slivers stacked behind say "there is more of this",
 * which is not the same sentence as "drag here". So the first car a device opens
 * carries a hint, and no car ever carries it again.
 *
 * ONCE PER DEVICE, which is why the flag cannot live in component state: this
 * unmounts on every swipe and remounts on the next car, so a boolean in React
 * would show the hint on every card — the exact nag it exists to avoid.
 *
 * It waits for the card to finish arriving, because a hint that lands during the
 * reveal is one more thing moving; then it leaves on its own, or the moment the
 * reader swipes — whichever comes first. Either way it is remembered, including
 * when they never waited for it to finish.
 *
 * `aria-hidden` and pointer-transparent. It describes a gesture nobody reading
 * by ear can make, and the deck's own previous/next controls are that reader's
 * path (NFR-04); and the drag it is asking for has to land on the card beneath
 * it rather than on the words about it.
 */

/** After the card has settled, so the hint is the only thing moving. */
const APPEARS_AFTER_MS = 1400;

/** Long enough to read twice, short enough not to become furniture. */
const STAYS_FOR_MS = 4200;

export default function CarDeckHint({ dismissed = false, storage = deviceMemory() }) {
    const t = useTranslate();
    // Read ONCE, on mount. The answer changes while this component is alive —
    // this is what changes it — and re-reading would hide the hint mid-show.
    const [isOffered] = useState(() => !hasSeen(DECK_HINT_SEEN, storage));
    const [isShowing, setShowing] = useState(false);

    useEffect(() => {
        if (!isOffered) {
            return undefined;
        }

        const shown = window.setTimeout(() => setShowing(true), APPEARS_AFTER_MS);
        const gone = window.setTimeout(() => {
            setShowing(false);
            rememberSeen(DECK_HINT_SEEN, storage);
        }, APPEARS_AFTER_MS + STAYS_FOR_MS);

        return () => {
            window.clearTimeout(shown);
            window.clearTimeout(gone);
        };
    }, [isOffered, storage]);

    // The reader has done the thing. There is nothing left to teach, and it is
    // remembered whether or not they sat through the hint.
    useEffect(() => {
        if (isOffered && dismissed) {
            setShowing(false);
            rememberSeen(DECK_HINT_SEEN, storage);
        }
    }, [dismissed, isOffered, storage]);

    if (!isOffered || !isShowing) {
        return null;
    }

    return (
        <p className="deck-hint" aria-hidden="true">
            <BackIcon size={18} />
            <span className="deck-hint__words">{t('cars.deck.swipeHint')}</span>
            <ForwardIcon size={18} />
        </p>
    );
}
